<template>
  <div>
    <v-container class="pt-3 pb-6 px-6">

      <!-- Download Excel -->
      <floating-button
        v-if="authUser && !work_order"
        :create="!isReadonly"
        @click="dialog = true"
      >
        <v-btn fab class="white--text" color="deep-orange" @click="exportExcel()">
          <v-icon>mdi-file-excel</v-icon>
        </v-btn>
      </floating-button>

      <!-- Confirmation -->
      <confirmation
        v-if="authUser && !work_order"
        v-model="confirmDialog"
        title="Eliminar Revisión de Intervención"
        text="Desea eliminar la revisión de intervención"
        :item="itemToDelete.code"
        @close="confirmDialog = false"
        @confirm="confirmDelete()"
      />

      <!-- Page Header -->
      <page-header
        v-if="!work_order && !showSelect"
        title="Revisión de Intervenciones"
        icon="mdi-magnify"
        divider="-"
        :breadcrumbs="breadcrumbs.items"
        refresh
        @refresh="init(true)"
      ></page-header>

      <!-- Content -->
      <v-row align="center" justify="center">
        <!-- Table -->
        <v-col cols="12">
          <!-- Loading Bar -->
          <v-progress-linear
            indeterminate
            v-if="isLoadingData"
          ></v-progress-linear>
          <v-data-table
            v-show="authUser"
            v-model="selected"
            :headers="filteredHeaders"
            :items="interventions"
            :class="{'elevation-1': !flat}"
            :expanded.sync="expanded"
            :show-expand="!showSelect"
            :show-select="showSelect"
            :options.sync="pagination"
            :server-items-length="pagination_total"
            :footer-props="{
              'items-per-page-options': [5, 50, 100, 250, 500]
            }"
            @update:options="init()"
          >
            <!-- Top -->
            <template v-slot:top>
              <v-toolbar flat color="white" v-if="!hideFilters">
                <!-- State Filter -->
                <v-select
                  v-if="!isClient"
                  v-model="filters.state_id"
                  :items="stateItems"
                  class="shrink mr-5"
                  label="Estado"
                  clearable
                  hide-details
                  single-line
                  @change="init()"
                ></v-select>
                <!-- Work Type -->
                <v-select
                  v-if="!isClient"
                  v-model="filters.work_type_id"
                  :items="workTypes"
                  class="shrink mr-5"
                  label="Tipo de trabajo"
                  clearable
                  hide-details
                  single-line
                  @change="init()"
                ></v-select>
                <!-- Technician -->
                <v-autocomplete
                  v-if="!isClient"
                  v-model="filters.technician_id"
                  :items="technicians"
                  label="Operario"
                  class="grow mr-5"
                  :item-text="text"
                  item-value="id"
                  clearable
                  single-line
                  hide-details
                  @change="init()"
                ></v-autocomplete>
                <!-- Farm -->
                <v-autocomplete
                  v-model="filters.wind_farm_id"
                  :items="farms"
                  label="Parque"
                  :class="isClient ? 'shrink' : 'grow'"
                  class="mr-5"
                  item-text="name"
                  item-value="id"
                  clearable
                  single-line
                  hide-details
                  @change="init()"
                ></v-autocomplete>
                <!-- Work Orders -->
                <v-autocomplete
                  v-if="!isClient"
                  v-model="filters.work_order_id"
                  :items="work_orders"
                  label="Código OT"
                  class="grow mr-5"
                  item-text="code"
                  item-value="id"
                  clearable
                  single-line
                  hide-details
                  @change="init()"
                ></v-autocomplete>
              </v-toolbar>
            </template>
            <!-- AG -->
            <template v-slot:item.ag="{ item }">
              {{ item.blade_damage[0] ? item.blade_damage[0].wind_turbine : '' }}
            </template>
            <!-- Start Date -->
            <template v-slot:item.start_date="{ item }">
              {{ item.work_intervention_day[0] ? formatDate(item.work_intervention_day[0].date) : '' }}
            </template>
            <!-- Operators -->
            <template v-slot:item.users="{ item }">
              <span v-if="item.users">
                <div v-for="(item, idx) in item.users" :key="item.id" >
                  <v-chip class="ma-1" light v-if="idx < 2">{{ operatorName(item) }}</v-chip>
                </div>
                <div class="pb-1" v-if="item.users.length > 2" >({{ item.users.length - 2 }} más)</div>
              </span>
            </template>
            <!-- Contract Award -->
            <template v-slot:item.contract_award="{ item }">
              {{ item.contract_award.client_code }}
            </template>
            <!-- State -->
            <template v-slot:item.state_id="{ item }">
              <v-chip :color="getColor(item.state_id)" :class="{'white--text': item.state_id !== STATE_TEXT.LISTA_PARA_VALIDAR}">
                {{ STATE_ID[item.state_id] }}
              </v-chip>
            </template>
            <!-- Actions -->
            <template v-slot:item.action="{ item }">
              <v-icon class="mr-2" @click="interventionReview(item)">{{ isReadonly ? 'mdi-eye' : 'mdi-pencil' }}</v-icon>
              <v-icon v-if="!isReadonly" @click="deleteconfirmDialog(item)">mdi-delete</v-icon>
            </template>
            <!-- Expanded Row -->
            <template v-slot:expanded-item="{ headers, item }">
              <!-- Summary -->
              <td :colspan="reportHeaders ? headers.length / 2 : 6" class="text-center py-3">
                <div class="mb-3 font-weight-bold caption">Resumen</div>
                <div class="my-4">
                  <span class="error--text">{{ item.blade_damage && item.blade_damage.length || 0 }}</span> Daños
                    /
                  <span class="error--text"> {{ item.work_intervention_day && item.work_intervention_day.length || 0 }}</span> Días
                </div>
              </td>
              <!-- Operators -->
              <td
                v-if="item.users && item.users.length"
                :colspan="reportHeaders ? headers.length / 2 : 1"
                class="text-center py-3"
              >
                <span class="mx-1" v-for="(item, idx) in item.users" :key="idx">
                  <v-chip class="my-1 text-capitalize" v-if="idx >= 2">{{ operatorName(item) }}</v-chip>
                </span>
              </td>
              <td :colspan="2"></td>
            </template>
            <!-- No Data -->
            <template v-slot:no-data>
              No hay elementos
            </template>
            <!-- No Results -->
            <template v-slot:no-results>
              No hay elementos
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <!-- Create Intervention -->
      <CreateInterventionDialog
        v-model="dialog"
        @close="dialog = false"
      />

    </v-container>
  </div>
</template>

<script>
import FloatingButton from "@/components/FloatingButton";
import Confirmation from "@/components/Confirmation";
import PageHeader from "@/components/PageHeader";
import CreateInterventionDialog from "@/components/InterventionReview/CreateInterventionDialog";
import Util from "@/util/index";
import STATES from '@/models/States'
import moment from 'moment'
import XLSX from 'xlsx'

export default {
  props: {
    work_order: { type: Boolean, default: false },
    flat: { type: Boolean, default: false },
    showSelect: { type: Boolean, default: false },
    hideFilters: { type: Boolean, default: false },
    reportHeaders: { type: Array, default: () => [] },
    reportItems: { type: Array, default: () => [] },
  },
  components: {
    FloatingButton,
    PageHeader,
    Confirmation,
    CreateInterventionDialog
  },
  data: () => ({
    expanded: [],
    selected: [],
    authUser: false,
    loadFilters: true,
    isLoadingData: false,
    dialog: false,
    confirmDialog: false,
    workTypes: STATES.WORK_ORDER_TYPES,
    STATE_ID: STATES.INTERVENTION_REVIEW_STATE_ID,
    STATE_TEXT: STATES.INTERVENTION_REVIEW_STATE_TEXT,
    stateItems: STATES.INTERVENTION_REVIEW_STATES,
    itemToDelete: {},
    filters: {
      state_id: '',
      work_type_id: '',
      technician_id: '',
      wind_farm_id: '',
      work_order_id: ''
    },
    pagination: {
      page: 1,
      itemsPerPage: 5,
      sortBy: [],
      sortDesc: []
    },
    pagination_total: -1,
    breadcrumbs: {
      items: [
        { text: "Configuración", disabled: true },
        { text: "Revisión de Intervenciones", disabled: true }
      ]
    },
    defaultHeaders: [
      { text: "Código Intervención", value: "id", align: "center", sortable: false },
      { text: "Tipo de trabajo", value: "work_order.work_type", align: "center" },
      { text: "Parque", value: "wind_farm.name", align: "center", client: true },
      { text: "AG", value: "ag", align: "center", sortable: false, client: true },
      { text: "Fecha", value: "start_date", align: "center", sortable: false, client: true },
      { text: "Operarios", value: "users", align: "center", sortable: false },
      { text: "Código OT", value: "work_order.code", align: "center" },
      { text: "Estado", value: "state_id", align: "center" },
      { text: "Acciones", value: "action", align: "center", sortable: false },
    ],
  }),
  watch: {
    selected(val) {
      this.$emit('selected', val)
    }
  },
  computed: {
    isClient () {
      return this.$store.getters['IS_CLIENT']
    },
    isReadonly() {
      return this.$store.getters['IS_READONLY']
    },
    headers () {
      return this.isClient
        ? this.defaultHeaders.filter(e => e.client)
        : this.reportHeaders.length
          ? this.reportHeaders
          : this.defaultHeaders
    },
    filteredHeaders () {
      return this.headers.filter(element => {
        return this.showSelect
          ? element.value !== 'action'
          : !this.work_order && element.value === 'action' || element.value !== 'action'
      })
    },
    farms () {
      return this.$store.state.farms.wind_farms.filter(element => element.is_active === true)
    },
    technicians () {
      return this.$store.state.users.all.filter(element => {
        return element.roles.filter(e => e.name === 'Operario').length > 0 && element.isActive === true
      })
    },
    work_orders () {
      return this.$store.state.orders.work_orders
    },
    interventions () {
      return this.reportHeaders.length
        ? this.reportItems
        : this.$store.state.interventions.list
    },
  },
  methods: {
    formatDate(date) {
      return Util.formatDate(date)
    },
    getColor(state) {
      if (state === this.STATE_TEXT.LISTA_PARA_VALIDAR) return ''
      if (state === this.STATE_TEXT.EN_REVISION) return 'deep-orange'
      if (state === this.STATE_TEXT.EN_CURSO) return 'info'
      if (state === this.STATE_TEXT.VALIDADA) return 'primary'
    },
    text (item) {
      return item.lastName2
        ? item.firstName + ' ' + item.lastName1 + ' ' + item.lastName2
        : item.firstName + ' ' + item.lastName1
    },
    operatorName (item) {
      return item.last_name_2
        ? item.first_name + ' ' + item.last_name_1 + ' ' + item.last_name_2
        : item.first_name + ' ' + item.last_name_1
    },
    exportExcel() {
      const data = []
      const obj = {}

      const exclude = ['expand', 'action']

      for (let i=0; i < this.headers.length; i++) {
        const element = this.headers[i]
        if (!exclude.includes(element.value)) obj[element.value] = element.text
      }

      data.push(obj)

      for (let i=0; i < this.interventions.length; i++) {
        let element = this.interventions[i]
        const obj = {
          id: element.id,
          'work_order.work_type': element.work_order.work_type,
          'wind_farm.name': element.wind_farm.name,
          ag: element.blade_damage[0] ? element.blade_damage[0].wind_turbine : '',
          start_date: element.work_intervention_day[0] ? this.formatDate(element.work_intervention_day[0].date) : '',
          users: '',
          'work_order.code': element.work_order.code,
          state_id: element.state
        }
        if (element.users) {
          const users = element.users.map(element => this.operatorName(element))
          obj.users = users.join(', ')
        }
        data.push(obj)
      }

      const name = 'rev_intervenciones_' + moment().format('DD_MM_YYYY') + '.xlsx'

      /* make the worksheet */
      const ws = XLSX.utils.json_to_sheet(data, { skipHeader: true })

      /* add to workbook */
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '');

      /* generate an XLSX file */
      XLSX.writeFile(wb, name)
    },
    deleteconfirmDialog (item) {
      this.itemToDelete = item
      this.confirmDialog = true
    },
    interventionReview (item) {
      this.$router.push({
        name: "intervention-review-edit",
        params: {
          work_order: item.work_order_id,
          id: item.id
        }
      });
    },
    confirmDelete () {
      this.$store.dispatch('interventions/delete', this.itemToDelete)
        .then(() => {
          this.$toast("Intervención borrada correctamente.", {
            color: "success",
            x: "center",
            y: "top",
            icon: "mdi-information"
          });
          this.init()
        })
        .catch(error => {
          var errorToastMessage = "Ha ocurrido un error eliminando la intervención."
          Util.showErrorToast(this, error, errorToastMessage);
        })
        .finally(() => {
          this.confirmDialog = !this.confirmDialog
        })
    },
    init (loadFilters) {
      if (this.reportHeaders.length) this.authUser = true
      else {
        this.isLoadingData = true;
        const body = {
          page: this.pagination.page,
          perPage: this.pagination.itemsPerPage,
          filters: this.filters,
          with: 'workOrder,technicians,windFarm,workInterventionDay,bladeDamage'
        }
        // Sort
        if (this.pagination.sortDesc.length) body.sortType = this.pagination.sortDesc[0] ? 'desc' : 'asc'
        if (this.pagination.sortBy.length) {
          const sortBy = this.pagination.sortBy[0]
          if (sortBy === 'wind_farm.name') body.sortBy = 'wind_farm'
          else if (sortBy === 'work_order.work_type') body.sortBy = 'work_type'
          else if (sortBy === 'work_order.code') body.sortBy = 'work_order_code'
          else body.sortBy = sortBy
        }
        this.$store.dispatch('interventions/list', body)
          .then(res => {
            this.authUser = true
            const meta = res.data.meta
            this.pagination_total = meta.total
          })
          .catch(error => {
            this.isLoadingData = false
            if (error && error.response && error.response.status === 403 ) {
              this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
                color: "error",
                x: "center",
                y: "top",
                icon: "mdi-information",
                timeout: 0,
                showClose: true,
                closeText: "Cerrar"
              });
              this.$router.push({ name: 'home'})
            } else {
              var errorToastMessage = "Ha ocurrido un error cargando las intervenciones."
              Util.showErrorToast(this, error, errorToastMessage);
            }
          })
          .finally(() => {
            if (this.loadFilters || loadFilters) this.loadFarms()
            else if (!this.loadFilters) this.isLoadingData = false
            this.loadFilters = false
          })
      }
    },
    loadFarms () {
      this.$store.dispatch('farms/getFarms')
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la lista parques eólicos."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          if (!this.isClient) this.loadUsers()
          else this.isLoadingData = false
        });
    },
    loadUsers() {
      this.$store.dispatch("users/getAllUsers")
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la lista de operarios."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          this.loadWorkOrders()
        });
    },
    loadWorkOrders () {
      this.$store.dispatch('orders/getOrders')
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la lista de órdenes de trabajo."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          this.isLoadingData = false
        })
    },
  },
  created () {
    // this.init()
  }
}
</script>
